import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, makeStyles } from '@material-ui/core';
import { useLogin, useNotify } from 'react-admin';

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minWidth: '300px',
        padding: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
}));

const TwoFactorVerification = ({ open, username, onVerify, onClose }) => {
    const classes = useStyles();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const mounted = useRef(true);
    const login = useLogin();
    const notify = useNotify();

    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const safeSetState = useCallback((setter) => {
        if (mounted.current) {
            setter();
        }
    }, []);

    const handleSubmit = useCallback(async (e) => {
        if (e) {
            e.preventDefault();
        }
        safeSetState(() => setLoading(true));
        try {
            const response = await fetch(`${localStorage.getItem('domain')}/api/users/verify-2fa`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-COMPANY': 'ROOT',
                    'Bypass-Tunnel-Reminder': 'true'
                },
                body: JSON.stringify({ username, code })
            });

            const data = await response.json();
            
            if (response.ok) {
                if (mounted.current) {
                    notify('Login successful', 'info');
                    onVerify({ token: data.token, roles: data.roles });
                }
            } else {
                safeSetState(() => setError(data.message || 'Invalid verification code'));
            }
        } catch (err) {
            safeSetState(() => setError('Failed to verify code. Please try again.'));
        } finally {
            safeSetState(() => setLoading(false));
        }
    }, [username, code, onVerify, notify, safeSetState]);

    const handleClose = useCallback(() => {
        safeSetState(() => {
            setCode('');
            setError('');
        });
        onClose();
    }, [onClose, safeSetState]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && code) {
            handleSubmit();
        }
    }, [code, handleSubmit]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            disableBackdropClick={true}
            disableEscapeKeyDown={loading}
        >
            <DialogTitle>Two-Factor Authentication</DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit} className={classes.form}>
                    <TextField
                        autoFocus
                        fullWidth
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyPress={handleKeyPress}
                        label="Verification Code"
                        variant="outlined"
                        inputProps={{ maxLength: 6 }}
                        error={!!error}
                        helperText={error || 'Please enter the verification code sent to your email'}
                        disabled={loading}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={!code || loading}
                >
                    {loading ? 'Verifying...' : 'Verify'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TwoFactorVerification; 